import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useRefreshData,
  useScoreAction,
  useActionStatus,
} from "../hooks/useActions";
import { useLlmModel } from "../hooks/useLlmModel";
import { useToast } from "../store/toast";
import { usePipeline } from "../store/pipeline";
import { fetchApiHealth } from "../lib/live-api";
import { queryKeys } from "../lib/query-client";

type ScoreMode = "missing" | "fallbacks" | "full" | "skill" | "cloud-skill";

interface Props {
  selectedSkill?: string | null;
  compact?: boolean;
  showAdvanced?: boolean;
}

const SCORE_ACTIONS: Array<{
  mode: ScoreMode;
  label: string;
  shortLabel?: string;
  confirm?: string;
  primary?: boolean;
  requiresSkill?: boolean;
}> = [
  { mode: "missing", label: "Score missing LLM", shortLabel: "Missing" },
  { mode: "fallbacks", label: "Re-score fallbacks", shortLabel: "Fallbacks" },
  {
    mode: "full",
    label: "Full combined score",
    shortLabel: "Full score",
    confirm: "Score all skills on bc-scan-arm? This can take several hours.",
  },
  { mode: "skill", label: "Re-score selected", requiresSkill: true, primary: true },
  { mode: "cloud-skill", label: "Cloud re-score (Groq/HF)", requiresSkill: true },
];

export function ActionsPanel({ selectedSkill, compact, showAdvanced = !compact }: Props) {
  const [moreOpen, setMoreOpen] = useState(false);
  const toast = useToast((s) => s.push);
  const setRun = usePipeline((s) => s.setRun);
  const { model, setModel, models } = useLlmModel();
  const refresh = useRefreshData();
  const score = useScoreAction();
  const runId = usePipeline((s) => s.runId);
  const status = useActionStatus(runId, runId != null);

  const { data: health } = useQuery({
    queryKey: queryKeys.health,
    queryFn: fetchApiHealth,
    staleTime: 120_000,
    retry: false,
  });

  const busy = refresh.isPending || score.isPending || (runId != null && !status.data?.completed);
  const actionsReady = health?.actionsReady === true;

  function trackRun(result: { run?: { id?: number; html_url?: string } | null }, label: string) {
    if (result.run?.id) {
      setRun(result.run.id, label);
      toast(`${label} — run #${result.run.id}`, {
        kind: "info",
        href: result.run.html_url,
        durationMs: 6000,
      });
    } else {
      toast(`${label} dispatched`, { kind: "info" });
    }
  }

  async function runScore(mode: ScoreMode, confirm?: string) {
    if (!actionsReady) {
      toast("Set GITHUB_TOKEN on Cloudflare Pages to enable actions", { kind: "error", durationMs: 8000 });
      return;
    }
    if (confirm && !window.confirm(confirm)) return;
    try {
      setMoreOpen(false);
      const payload =
        mode === "skill"
          ? { mode, skill: selectedSkill!, model }
          : { mode, model };
      const r = await score.mutateAsync(payload);
      trackRun(r, SCORE_ACTIONS.find((a) => a.mode === mode)?.label ?? "Score");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Score failed", { kind: "error" });
    }
  }

  const btn =
    "border border-fg px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.08em] hover:bg-[var(--hover)] disabled:opacity-50";

  const visibleScoreActions = SCORE_ACTIONS.filter((a) => {
    if (a.requiresSkill && !selectedSkill) return false;
    if ((a.mode === "full" || a.mode === "fallbacks") && !showAdvanced) return false;
    return true;
  });

  const primaryActions = compact
    ? visibleScoreActions.filter((a) => a.mode === "missing" || a.mode === "skill")
    : visibleScoreActions;

  const advancedActions = compact
    ? visibleScoreActions.filter((a) => a.mode === "full" || a.mode === "fallbacks")
    : [];

  const modelSelect = (
    <label className={`flex items-center gap-1.5 min-w-0 ${compact ? "model-select-compact" : "w-full"}`}>
      <span className="font-mono text-[10px] uppercase text-muted shrink-0">Model</span>
      <select
        value={model}
        onChange={(e) => setModel(e.target.value)}
        disabled={busy}
        className="border border-fg bg-bg px-2 py-1 font-mono text-[10px] max-w-[200px] min-w-0 truncate"
        title={model}
      >
        {models.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className={compact ? "flex flex-wrap items-center gap-2" : "border border-fg p-3 space-y-2"}>
      {!compact && (
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted font-medium flex flex-wrap gap-x-3">
          <span>// Actions · bc-scan-arm</span>
          {health && !actionsReady && (
            <span className="text-warn" title="Run scripts/setup-pages-github-token.sh">
              GITHUB_TOKEN missing on Pages
            </span>
          )}
        </div>
      )}
      {!compact && modelSelect}
      <div className="flex flex-wrap gap-2 items-center">
        {compact && modelSelect}
        <button
          type="button"
          className={btn}
          disabled={busy}
          onClick={async () => {
            if (!actionsReady) {
              toast("Set GITHUB_TOKEN on Cloudflare Pages", { kind: "error" });
              return;
            }
            try {
              const r = await refresh.mutateAsync();
              trackRun(r, "Refresh data");
            } catch (e) {
              toast(e instanceof Error ? e.message : "Refresh failed", { kind: "error" });
            }
          }}
        >
          {compact ? "Sync" : "Refresh data"}
        </button>

        {primaryActions.map((a) => (
          <button
            key={a.mode}
            type="button"
            className={`${btn} ${a.primary ? "bg-fg text-bg font-medium" : ""}`}
            disabled={busy}
            onClick={() => void runScore(a.mode, a.confirm)}
            title={`${a.label} · ${model}`}
          >
            {compact && a.shortLabel
              ? a.mode === "skill" && selectedSkill
                ? "Re-score"
                : a.shortLabel
              : a.mode === "skill" && selectedSkill
                ? `Re-score ${selectedSkill}`
                : a.label}
          </button>
        ))}

        {compact && advancedActions.length > 0 && (
          <div className="relative">
            <button
              type="button"
              className={btn}
              disabled={busy}
              onClick={() => setMoreOpen(!moreOpen)}
              aria-expanded={moreOpen}
            >
              More ▾
            </button>
            {moreOpen && (
              <div className="absolute left-0 top-full mt-1 border border-fg bg-bg z-40 flex flex-col min-w-[160px] shadow-[4px_4px_0_0_var(--fg)]">
                {advancedActions.map((a) => (
                  <button
                    key={a.mode}
                    type="button"
                    className={`${btn} border-0 border-b border-rule last:border-b-0 rounded-none text-left`}
                    disabled={busy}
                    onClick={() => void runScore(a.mode, a.confirm)}
                  >
                    {a.shortLabel ?? a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}